import { MapPin, Phone, Mail, Clock, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import BouquetLeadForm from "@/components/BouquetLeadForm";
import { CITIES, COUNTRIES } from "@/cityConfig";

const citiesByCountry = Object.values(CITIES).reduce((acc, c) => {
  if (!acc[c.country]) acc[c.country] = [];
  acc[c.country].push(c);
  return acc;
}, {});

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export const Footer = ({ settings, city, lang = "ru", t }) => {
  const cityPrep = lang === "tr" ? (city?.namePrepositionalTr || city?.namePrepositionalEn) : (lang === "en" ? city?.namePrepositionalEn : city?.namePrepositional);
  const cityNameDisplay = lang === "tr" ? (city?.nameTr || city?.nameEn) : (lang === "en" ? city?.nameEn : city?.name);
  const countryNameDisplay = lang === "tr" ? "Turkiye" : (lang === "en" ? city?.countryNameEn : city?.countryName);

  return (
    <footer className="bg-black text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 sm:gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <a href="/" className="inline-block mb-6">
              <Logo variant="light" size="large" />
            </a>
            <p className="text-gray-300 mb-6 max-w-sm text-lg">
              {t ? t("footer.desc", { city: cityPrep }) : ""}
            </p>
            
            <a 
              href={`https://wa.me/${settings?.whatsapp?.replace(/[^0-9]/g, "") || "905316224378"}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25d366] hover:bg-[#20bd5a] text-white px-6 py-3 rounded-full font-bold transition-all hover:scale-105 shadow-lg"
            >
              <WhatsAppIcon />
              <span>{t ? t("footer.whatsapp") : "Написать в WhatsApp"}</span>
            </a>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-pink-400">{t ? t("footer.contacts") : "Контакты"}</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`tel:${settings?.whatsapp || "+905316224378"}`}
                  className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-pink-400" />
                  </div>
                  <span className="font-medium">{settings?.whatsapp || "+90 531 622 43 78"}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${settings?.email || "info@yuccaflower.com"}`}
                  className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-pink-400" />
                  </div>
                  <span className="font-medium">{settings?.email || "info@yuccaflower.com"}</span>
                </a>
              </li>
              <li className="flex items-center gap-3 text-gray-300">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-pink-400" />
                </div>
                <span className="font-medium">{cityNameDisplay || "Antalya"}, {countryNameDisplay || "Turkey"}</span>
              </li>
            </ul>
          </div>

          {/* Info */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-pink-400">{t ? t("footer.info") : "Информация"}</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-gray-300">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-pink-400" />
                </div>
                <span className="font-medium">{settings?.working_hours || "09:00 - 21:00"}</span>
              </li>
              <li className="text-gray-300 pl-[52px] font-medium">
                {t ? t("footer.sameDay") : "Доставка в день заказа"}
              </li>
              <li className="text-gray-300 pl-[52px] font-medium">
                {t ? t("footer.anyPayment") : "Оплата любой валютой"}
              </li>
            </ul>
          </div>

          {/* Cities by country (SEO internal linking) */}
          <div data-testid="footer-cities">
            <h4 className="text-lg font-bold mb-6 text-pink-400">
              {lang === "tr" ? "Sehirler" : lang === "en" ? "Cities" : "Города"}
            </h4>
            <ul className="space-y-4 text-sm">
              {Object.values(COUNTRIES).map((country) => {
                const cities = citiesByCountry[country.slug] || [];
                if (cities.length === 0) return null;
                const countryLabel = lang === "tr" ? country.nameTr : lang === "en" ? country.nameEn : country.name;
                return (
                  <li key={country.slug}>
                    <div className="flex items-center gap-1.5 text-gray-400 font-semibold mb-1.5">
                      <span aria-hidden="true">{country.flag}</span>
                      <span>{countryLabel}</span>
                    </div>
                    <ul className="flex flex-wrap gap-x-3 gap-y-1 pl-1">
                      {cities.map((c) => (
                        <li key={c.slug}>
                          <Link
                            to={`/${c.slug}${lang !== "ru" ? `?lang=${lang}` : ""}`}
                            className="text-gray-300 hover:text-pink-400 transition-colors"
                            data-testid={`footer-city-${c.slug}`}
                          >
                            {lang === "tr" ? (c.nameTr || c.nameEn) : lang === "en" ? c.nameEn : c.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Custom bouquet lead form — sends Telegram notification */}
        <div className="mt-12 pt-12 border-t border-white/10 max-w-2xl mx-auto" data-testid="footer-bouquet-form">
          <BouquetLeadForm city={city?.slug || ""} lang={lang} variant="compact" />
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-center sm:text-left">
          <p className="text-gray-400 text-sm">
            &copy; 2024 YUCCA flowers. {t ? t("footer.copy") : "Сделано с"}{" "}
            <Heart className="w-4 h-4 text-pink-500 fill-pink-500 inline-block align-middle mx-0.5" />{" "}
            {cityPrep || ""}
          </p>
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-x-4 gap-y-2 text-sm max-w-full" data-testid="footer-links">
            <a href="#reviews" className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-reviews-link">
              {lang === "tr" ? "Yorumlar" : lang === "en" ? "Reviews" : "Отзывы"}
            </a>
            <Link to="/blog" className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-blog-link">
              {lang === "tr" ? "Blog" : lang === "en" ? "Blog" : "Блог"}
            </Link>
            <Link to="/press-about-us" className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-press-link">
              {lang === "tr" ? "Basında Biz" : lang === "en" ? "Press" : "Пресса о нас"}
            </Link>
            <Link to="/terms" className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-terms-link">
              {lang === "tr" ? "Kosullar" : lang === "en" ? "Terms" : "Соглашение"}
            </Link>
            <Link to="/privacy" className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-privacy-link">
              {lang === "tr" ? "Gizlilik" : lang === "en" ? "Privacy" : "Конфиденциальность"}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
